<template>
  <ul
    :id="'sb-multiselect-list-' + inputId"
    :anchor="'sb-combobox-wrapper-' + inputId"
    :aria-labelledby="'sb-multiselect-list-' + inputId"
    :style="`position-anchor: --dialog-${inputId};`"
    class="sb-multiselect-list"
    role="listbox"
    :aria-multiselectable="true"
    tabindex="-1"
    :aria-activedescendant="`sb-multiselect-list-item-${computedActiveDecendant}`"
    @keydown="handleKeyDown"
  >
    <li
      v-if="showTagCreation"
      class="sb-multiselect-list__create"
      @keydown.enter="handleOptionCreated(searchInput)"
      @click="handleOptionCreated(searchInput)"
    >
      <span class="sb-multiselect-list__create-label">Create</span>
      <span
        class="sb-multiselect-list__create-value"
        :title="searchInput"
        :data-testid="`${dataTestid}-create-search`"
      >
        {{ searchInput }}
      </span>
    </li>
    <SbSelectListItem
      v-if="selectAllVisible"
      class="sb-multiselect-list__select-all"
      :data-testid="`${dataTestid}-all-option`"
      :label="selectAllLabel"
      :is-disabled="allOptionsDisabled"
      :all-selected="allOptionsSelected"
      :input-id="inputId"
      :is-indeterminate="isIndeterminate"
      :select-all-option="selectAllVisible"
      @emit-value="handleClickSelectAll"
    />
    <template v-if="hasOptions">
      <SbSelectListItem
        v-for="(option, index) in options"
        :key="index"
        :option="option"
        :label="option[itemLabel] as string"
        :model-value="option[itemValue]"
        :item-value="itemValue"
        :input-value="modelValue"
        :emit-option="emitOption"
        :use-avatars="useAvatars"
        :show-caption="showCaption"
        :caption="option[itemCaption] as string"
        :is-disabled="isOptionDisabled(option)"
        :aria-disabled="isOptionDisabled(option)"
        :input-id="inputId"
        :data-testid="`${dataTestid}-item__${index}`"
        @emit-value="handleEmitValue"
      />
    </template>
    <li v-if="isLoadingMore">
      <span
        class="sb-multiselect-list__empty"
        :data-testid="`${dataTestid}-loading-more`"
      >
        <SbLoading color="primary" size="normal" />
        {{ loadingMoreText }}
      </span>
    </li>
    <li
      v-else-if="showTextStartingTagCreation"
      :data-testid="`${dataTestid}-no-data-text-tag`"
    >
      <span class="sb-multiselect-list__empty"> {{ noDataTextTag }} </span>
    </li>
    <li
      v-else-if="!hasOptions && !showTagCreation && !isLoading"
      :data-testid="`${dataTestid}-no-data-text`"
    >
      <span class="sb-multiselect-list__empty">{{ noDataText }}</span>
    </li>
  </ul>
</template>

<script lang="ts">
import type { PropType } from 'vue'

import { toLowerCase } from '../../../utils'
import SbLoading from '../../Loading'
import SbSelectListItem from './SelectListItem.vue'
import type { MultiSelectValue, Option } from '../types'
import { useSelectContext } from '../../Select/helpers'

export default {
  name: 'SbSelectList',

  components: {
    SbSelectListItem,
    SbLoading,
  },

  props: {
    // input itself value
    modelValue: {
      type: Array as PropType<MultiSelectValue[]>,
      default: null,
    },

    // options
    allowCreate: Boolean,
    emitOption: Boolean,
    isLoading: Boolean,
    options: {
      type: Array as PropType<Option[]>,
      required: true,
      default: () => [],
    },
    useAvatars: Boolean,
    itemLabel: {
      type: String,
      default: 'label',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    noDataText: {
      type: String,
      required: true,
    },
    noDataTextTag: {
      type: String,
      required: true,
    },
    searchInput: {
      type: String,
      default: undefined,
    },

    inputId: {
      type: String,
      default: '',
    },
    showCaption: Boolean,
    itemCaption: {
      type: String,
      default: 'caption',
    },
    isLoadingMore: Boolean,
    loadingMoreText: {
      type: String,
      required: true,
    },
    selectAllVisible: Boolean,
    isAllOptionDisabled: Boolean,
    selectAllLabel: {
      type: String,
      default: 'Select all',
    },
    isOptionDisabled: {
      type: Function,
      default: () => false,
    },
  },

  emits: [
    'emit-value',
    'focus-item',
    'keydown',
    'option-created',
    'select-all',
  ],

  setup() {
    const selectContext = useSelectContext()

    return {
      selectContext,
    }
  },

  data() {
    return {
      focusedItem: -1,
      allOption: false,
    }
  },

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-multiselect-list'
    },

    context() {
      return this.selectContext()
    },

    hasOptions() {
      return this.options.length
    },

    hasPartialMatch() {
      let partialMatches = 0
      this.options.forEach((option) => {
        if (
          toLowerCase(option.name as string)?.indexOf(
            toLowerCase(this.searchInput),
          ) !== -1
        ) {
          partialMatches++
        }
      })

      return partialMatches
    },

    hasExactMatch() {
      let exactMatch = 0
      this.options.forEach((option) => {
        if (
          toLowerCase(option.name as string) === toLowerCase(this.searchInput)
        ) {
          exactMatch++
        }
      })

      return exactMatch
    },

    showTagCreation() {
      return (
        (this.hasPartialMatch || !this.hasOptions) &&
        !this.hasExactMatch &&
        this.allowCreate &&
        this.searchInput
      )
    },

    showTextStartingTagCreation() {
      return (
        !this.hasOptions &&
        !this.showTagCreation &&
        !this.hasExactMatch &&
        this.allowCreate
      )
    },

    selectedOptions() {
      return this.options.filter(
        (option) =>
          Array.isArray(this.modelValue) &&
          this.modelValue.includes(option[this.itemValue]),
      )
    },

    allOptionsSelected() {
      return this.options.every(
        (option) =>
          Array.isArray(this.modelValue) &&
          this.modelValue.includes(option[this.itemValue]),
      )
    },

    allOptionsDisabled() {
      return (
        this.options.every((option) => this.isOptionDisabled(option)) ||
        this.isAllOptionDisabled
      )
    },

    computedActiveDecendant() {
      return this.selectedOptions?.[0]?.value || this.options?.[0]?.value
    },

    isIndeterminate() {
      return !this.allOptionsSelected && !!this.selectedOptions.length
    },
  },

  watch: {
    'context.activeIndex'(value) {
      if (value === -1) {
        this.focusedItem = value
      } else {
        this.handleFocusItem(value)
      }
    },
  },

  methods: {
    /**
     * forward the 'emit-value' event
     * @param {String} value
     */
    handleEmitValue(value: MultiSelectValue) {
      this.$emit('emit-value', value)
    },

    /**
     * forward the 'optione-created' event
     * @param {String} value
     */
    handleOptionCreated(value: string | undefined) {
      this.$emit('option-created', value)
    },

    /**
     * handle with keydown event when list component is focused
     * @param {Event} event
     */
    handleKeyDown(event: KeyboardEvent) {
      const {
        activeIndex,
        focusAtIndex,
        focusOnFirstItem,
        focusOnLastItem,
        hideList,
        listItems,
      } = this.context
      const count = listItems?.length || 0

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          focusAtIndex((activeIndex + 1) % count)
          break
        case 'ArrowUp':
          event.preventDefault()
          focusAtIndex((activeIndex - 1 + count) % count)
          break
        case 'Home':
          this.handleFocusItem(0)
          focusOnFirstItem()
          break
        case 'End':
          this.handleFocusItem(count - 1)
          focusOnLastItem()
          break
        case 'Tab':
          event.preventDefault()
          break
        case 'Escape':
          hideList()
          break
        default:
          break
      }

      this.$emit('keydown', event)
    },

    handleFocusItem(index: number) {
      this.focusedItem = index
      this.$emit('focus-item', index)
    },

    handleClickSelectAll() {
      this.$emit('select-all')
    },
  },
}
</script>
